import { useState, useEffect, useCallback } from 'react';
import { Shield, ShieldCheck, Copy, Check, AlertTriangle, KeyRound, Mail } from 'lucide-react';
import {
  getTotpStatus, setupTotp, verifyTotpSetup, disableTotp, regenerateRecoveryCodes,
  enableEmail2fa, disableEmail2fa,
  type TotpStatus, type TotpSetupResponse,
} from '../api';

type Mode = 'view' | 'enrolling' | 'disabling' | 'regen-recovery' | 'email-enable' | 'email-disable';

export default function TwoFactorSection() {
  const [status, setStatus]   = useState<TotpStatus | null>(null);
  const [mode, setMode]       = useState<Mode>('view');
  const [enrollment, setEnrollment] = useState<TotpSetupResponse | null>(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [error, setError]     = useState('');
  const [working, setWorking] = useState(false);

  // For disable
  const [disablePw, setDisablePw] = useState('');
  const [disableCode, setDisableCode] = useState('');

  // For regen recovery
  const [regenCode, setRegenCode] = useState('');
  const [newRecoveryCodes, setNewRecoveryCodes] = useState<string[] | null>(null);

  // For email-2FA enable/disable
  const [emailEnablePw, setEmailEnablePw] = useState('');
  const [emailDisablePw, setEmailDisablePw] = useState('');
  const [emailDisableCode, setEmailDisableCode] = useState('');

  const reload = useCallback(() => getTotpStatus().then(setStatus), []);
  useEffect(() => { reload(); }, [reload]);

  const startEnroll = async () => {
    setError(''); setWorking(true);
    try {
      const data = await setupTotp();
      setEnrollment(data);
      setMode('enrolling');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fejl');
    } finally { setWorking(false); }
  };

  const confirmEnroll = async () => {
    setError(''); setWorking(true);
    try {
      await verifyTotpSetup(verifyCode.trim());
      setMode('view');
      setEnrollment(null);
      setVerifyCode('');
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Forkert kode');
    } finally { setWorking(false); }
  };

  const confirmDisable = async () => {
    setError(''); setWorking(true);
    try {
      await disableTotp(disablePw, disableCode.trim());
      setMode('view');
      setDisablePw(''); setDisableCode('');
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fejl');
    } finally { setWorking(false); }
  };

  const confirmRegen = async () => {
    setError(''); setWorking(true);
    try {
      const r = await regenerateRecoveryCodes(regenCode.trim());
      setNewRecoveryCodes(r.recovery_codes);
      setRegenCode('');
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fejl');
    } finally { setWorking(false); }
  };

  const confirmEmailEnable = async () => {
    setError(''); setWorking(true);
    try {
      await enableEmail2fa(emailEnablePw);
      setEmailEnablePw('');
      setMode('view');
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fejl');
    } finally { setWorking(false); }
  };

  const confirmEmailDisable = async () => {
    setError(''); setWorking(true);
    try {
      await disableEmail2fa(emailDisablePw, emailDisableCode || undefined);
      setEmailDisablePw(''); setEmailDisableCode('');
      setMode('view');
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fejl');
    } finally { setWorking(false); }
  };

  if (!status) return null;

  // ── VIEW ────────────────────────────────────────────────────────────────
  if (mode === 'view') {
    return (
      <div className="space-y-3">
        {/* TOTP-kort */}
        <div className="bg-cream-50 rounded-card shadow-card-sm p-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <span className={`w-10 h-10 rounded-lg flex items-center justify-center ${status.enabled ? 'bg-sage-100 text-sage-700' : 'bg-cream-200 text-ink-500'}`}>
                {status.enabled ? <ShieldCheck size={20} /> : <Shield size={20} />}
              </span>
              <div>
                <h3 className="text-base font-semibold text-ink-900">Authenticator-app (TOTP)</h3>
                <p className="text-sm text-ink-500 mt-1">
                  {status.enabled
                    ? `Aktiveret. ${status.recovery_codes_remaining} recovery-koder tilbage.`
                    : 'Google Authenticator, 1Password, Authy m.fl. Anbefalet — mest sikker.'}
                </p>
              </div>
            </div>
            {status.enabled ? (
              <div className="flex gap-2">
                <button onClick={() => setMode('regen-recovery')} className="btn btn-secondary text-sm py-1.5 px-3">
                  Nye recovery-koder
                </button>
                <button onClick={() => setMode('disabling')} className="btn btn-danger-outline text-sm py-1.5 px-3">
                  Slå fra
                </button>
              </div>
            ) : (
              <button onClick={startEnroll} disabled={working} className="btn btn-primary">
                {working ? 'Starter…' : 'Aktivér'}
              </button>
            )}
          </div>
        </div>

        {/* Email-2FA kort */}
        <div className="bg-cream-50 rounded-card shadow-card-sm p-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <span className={`w-10 h-10 rounded-lg flex items-center justify-center ${status.email_2fa_enabled ? 'bg-sage-100 text-sage-700' : 'bg-cream-200 text-ink-500'}`}>
                <Mail size={20} />
              </span>
              <div>
                <h3 className="text-base font-semibold text-ink-900">Email-kode (2FA)</h3>
                <p className="text-sm text-ink-500 mt-1">
                  {status.email_2fa_enabled
                    ? 'Aktiveret. Kode sendes til din email ved login eller følsomme aktioner.'
                    : 'Alternativ til authenticator-app. Mindre sikker (transit via mail) men nem fallback hvis du mister telefonen.'}
                </p>
              </div>
            </div>
            {status.email_2fa_enabled ? (
              <button onClick={() => setMode('email-disable')} className="btn btn-danger-outline text-sm py-1.5 px-3">
                Slå fra
              </button>
            ) : (
              <button onClick={() => setMode('email-enable')} className="btn btn-primary">
                Aktivér
              </button>
            )}
          </div>
        </div>

        {error && <p className="text-sm text-rose-700">{error}</p>}
      </div>
    );
  }

  // ── EMAIL-ENABLE ────────────────────────────────────────────────────────
  if (mode === 'email-enable') {
    return (
      <div className="bg-cream-50 rounded-card shadow-card-sm p-5">
        <div className="flex items-center gap-2.5 mb-3">
          <span className="w-9 h-9 rounded-lg bg-sage-100 text-sage-700 flex items-center justify-center">
            <Mail size={18} />
          </span>
          <h3 className="modal-title">Aktivér email-2FA</h3>
        </div>
        <p className="text-sm text-ink-500 mb-4">
          Bekræft med dit password. Når aktivt vil du modtage en 6-cifret kode på email ved login eller backup-download.
        </p>
        <input type="password" value={emailEnablePw} onChange={e => setEmailEnablePw(e.target.value)}
          placeholder="Dit password" className="input" autoComplete="current-password" />
        {error && <p className="text-sm text-rose-700 mt-3">{error}</p>}
        <div className="flex gap-2 mt-4">
          <button onClick={confirmEmailEnable} disabled={working || !emailEnablePw} className="btn btn-primary">
            {working ? 'Aktiverer…' : 'Aktivér email-2FA'}
          </button>
          <button onClick={() => { setMode('view'); setEmailEnablePw(''); setError(''); }} className="btn btn-secondary">
            Annuller
          </button>
        </div>
      </div>
    );
  }

  // ── EMAIL-DISABLE ───────────────────────────────────────────────────────
  if (mode === 'email-disable') {
    const requiresCode = status.enabled;  // hvis TOTP også er aktivt kræver vi en kode
    return (
      <div className="bg-cream-50 rounded-card shadow-card-sm p-5">
        <div className="flex items-center gap-2.5 mb-3">
          <span className="w-9 h-9 rounded-lg bg-rose-100 text-rose-700 flex items-center justify-center">
            <AlertTriangle size={18} />
          </span>
          <h3 className="modal-title">Deaktivér email-2FA</h3>
        </div>
        <p className="text-sm text-ink-500 mb-4">
          {requiresCode
            ? 'Bekræft med dit password OG en frisk TOTP-kode (siden TOTP er aktivt).'
            : 'Bekræft med dit password.'}
        </p>
        <div className="space-y-3">
          <input type="password" value={emailDisablePw} onChange={e => setEmailDisablePw(e.target.value)}
            placeholder="Dit password" className="input" autoComplete="current-password" />
          {requiresCode && (
            <input type="text" value={emailDisableCode} onChange={e => setEmailDisableCode(e.target.value)}
              placeholder="TOTP-kode (6 cifre)" className="input font-mono" />
          )}
        </div>
        {error && <p className="text-sm text-rose-700 mt-3">{error}</p>}
        <div className="flex gap-2 mt-4">
          <button onClick={confirmEmailDisable} disabled={working || !emailDisablePw || (requiresCode && !emailDisableCode)} className="btn btn-danger">
            {working ? 'Deaktiverer…' : 'Bekræft deaktivering'}
          </button>
          <button onClick={() => { setMode('view'); setEmailDisablePw(''); setEmailDisableCode(''); setError(''); }} className="btn btn-secondary">
            Annuller
          </button>
        </div>
      </div>
    );
  }

  // ── ENROLLING ───────────────────────────────────────────────────────────
  if (mode === 'enrolling' && enrollment) {
    return (
      <EnrollmentFlow
        enrollment={enrollment}
        verifyCode={verifyCode}
        setVerifyCode={setVerifyCode}
        onConfirm={confirmEnroll}
        onCancel={() => { setMode('view'); setEnrollment(null); setVerifyCode(''); }}
        working={working}
        error={error}
      />
    );
  }

  // ── DISABLING ───────────────────────────────────────────────────────────
  if (mode === 'disabling') {
    return (
      <div className="bg-cream-50 rounded-card shadow-card-sm p-5">
        <div className="flex items-center gap-2.5 mb-3">
          <span className="w-9 h-9 rounded-lg bg-rose-100 text-rose-700 flex items-center justify-center">
            <AlertTriangle size={18} />
          </span>
          <h3 className="modal-title">Deaktivér 2FA</h3>
        </div>
        <p className="text-sm text-ink-500 mb-4">
          Bekræft med dit password OG en frisk TOTP/recovery-kode for at sikre at det er dig.
        </p>
        <div className="space-y-3">
          <input type="password" value={disablePw} onChange={e => setDisablePw(e.target.value)}
            placeholder="Dit password" className="input" autoComplete="current-password" />
          <input type="text" value={disableCode} onChange={e => setDisableCode(e.target.value)}
            placeholder="TOTP-kode (6 cifre) eller recovery (XXXX-XXXX)" className="input font-mono" />
        </div>
        {error && <p className="text-sm text-rose-700 mt-3">{error}</p>}
        <div className="flex gap-2 mt-4">
          <button onClick={confirmDisable} disabled={working || !disablePw || !disableCode} className="btn btn-danger">
            {working ? 'Deaktiverer…' : 'Bekræft deaktivering'}
          </button>
          <button onClick={() => { setMode('view'); setDisablePw(''); setDisableCode(''); setError(''); }} className="btn btn-secondary">
            Annuller
          </button>
        </div>
      </div>
    );
  }

  // ── REGEN RECOVERY ──────────────────────────────────────────────────────
  if (mode === 'regen-recovery') {
    if (newRecoveryCodes) {
      return (
        <div className="bg-cream-50 rounded-card shadow-card-sm p-5">
          <div className="flex items-center gap-2.5 mb-3">
            <span className="w-9 h-9 rounded-lg bg-sage-100 text-sage-700 flex items-center justify-center">
              <KeyRound size={18} />
            </span>
            <h3 className="modal-title">Nye recovery-koder</h3>
          </div>
          <RecoveryCodesDisplay codes={newRecoveryCodes} />
          <button onClick={() => { setNewRecoveryCodes(null); setMode('view'); }} className="btn btn-primary mt-4">
            Jeg har gemt dem
          </button>
        </div>
      );
    }
    return (
      <div className="bg-cream-50 rounded-card shadow-card-sm p-5">
        <h3 className="modal-title mb-2">Generér nye recovery-koder</h3>
        <p className="text-sm text-ink-500 mb-4">De gamle koder bliver ugyldige. Bekræft med en TOTP-kode.</p>
        <input type="text" value={regenCode} onChange={e => setRegenCode(e.target.value)}
          placeholder="6-cifret kode" className="input font-mono" />
        {error && <p className="text-sm text-rose-700 mt-3">{error}</p>}
        <div className="flex gap-2 mt-4">
          <button onClick={confirmRegen} disabled={working || !regenCode} className="btn btn-primary">
            {working ? 'Genererer…' : 'Generér nye koder'}
          </button>
          <button onClick={() => { setMode('view'); setRegenCode(''); setError(''); }} className="btn btn-secondary">
            Annuller
          </button>
        </div>
      </div>
    );
  }

  return null;
}

// ─── Enrollment-flow ────────────────────────────────────────────────────────

function EnrollmentFlow({ enrollment, verifyCode, setVerifyCode, onConfirm, onCancel, working, error }: {
  enrollment: TotpSetupResponse;
  verifyCode: string;
  setVerifyCode: (s: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
  working: boolean;
  error: string;
}) {
  const [secretCopied, setSecretCopied] = useState(false);

  const copySecret = () => {
    navigator.clipboard.writeText(enrollment.secret);
    setSecretCopied(true);
    setTimeout(() => setSecretCopied(false), 2000);
  };

  return (
    <div className="bg-cream-50 rounded-card shadow-card-sm p-5">
      <h3 className="modal-title mb-1">Aktivér 2FA — 3 trin</h3>
      <p className="text-sm text-ink-500 mb-5">Brug Google Authenticator, 1Password, Authy eller anden TOTP-app.</p>

      {/* Trin 1: scan QR */}
      <div className="mb-6">
        <h4 className="text-sm font-semibold text-ink-900 mb-2">1. Scan QR-koden med din app</h4>
        <div className="flex gap-5 items-start">
          <img src={enrollment.qr_data_url} alt="QR-kode" className="rounded-lg border border-cream-100 w-40 h-40" />
          <div className="text-sm text-ink-500">
            <p className="mb-2">Eller paste denne nøgle manuelt i appen:</p>
            <div className="flex items-center gap-2 bg-cream-100 rounded-lg px-3 py-2 font-mono text-xs">
              <span className="text-ink-900 break-all">{enrollment.secret}</span>
              <button onClick={copySecret} className="text-sage-700 hover:text-sage-900 shrink-0" title="Kopiér">
                {secretCopied ? <Check size={14} /> : <Copy size={14} />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Trin 2: gem recovery codes */}
      <div className="mb-6">
        <h4 className="text-sm font-semibold text-ink-900 mb-2">2. Gem dine recovery-koder</h4>
        <p className="text-sm text-ink-500 mb-3">
          Hvis du mister din telefon, kan du logge ind med en af disse koder.
          <strong className="text-rose-700"> De vises kun nu — gem dem et sikkert sted (fx 1Password).</strong>
        </p>
        <RecoveryCodesDisplay codes={enrollment.recovery_codes} />
      </div>

      {/* Trin 3: verificér */}
      <div className="mb-4">
        <h4 className="text-sm font-semibold text-ink-900 mb-2">3. Indtast koden fra din app for at bekræfte</h4>
        <input
          type="text"
          value={verifyCode}
          onChange={e => setVerifyCode(e.target.value)}
          placeholder="123 456"
          inputMode="numeric"
          autoComplete="one-time-code"
          className="input text-center tracking-widest text-lg font-mono max-w-[200px]"
        />
      </div>

      {error && <p className="text-sm text-rose-700 mt-2">{error}</p>}

      <div className="flex gap-2 mt-4">
        <button onClick={onConfirm} disabled={working || !verifyCode.trim()} className="btn btn-primary">
          {working ? 'Verificerer…' : 'Aktivér 2FA'}
        </button>
        <button onClick={onCancel} className="btn btn-secondary">
          Annuller
        </button>
      </div>
    </div>
  );
}

function RecoveryCodesDisplay({ codes }: { codes: string[] }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(codes.join('\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="bg-cream-100 rounded-lg p-4">
      <div className="grid grid-cols-2 gap-1.5 font-mono text-sm text-ink-900">
        {codes.map((c, i) => <div key={i}>{c}</div>)}
      </div>
      <button onClick={copy} className="mt-3 text-xs text-sage-700 hover:text-sage-900 inline-flex items-center gap-1.5">
        {copied ? <Check size={13} /> : <Copy size={13} />}
        {copied ? 'Kopieret' : 'Kopiér alle koder'}
      </button>
    </div>
  );
}
